import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";

export interface PaymentSettings {
  paymentRequestTemplate: string;
  paymentReminderTemplate: string;
  paymentReceivedTemplate: string;
  estimateTemplate: string;
  includeLogo: boolean;
  includeAddress: boolean;
  includeContactInfo: boolean;
  includeTaxId: boolean;
  taxId: string;
  includeThankYouMessage: boolean;
  customMessage: string;
  reminderIntervalDays: number;
  paymentDueDays: number;
  smsDisabled: boolean;
}

export const DEFAULT_PAYMENT_REQUEST_TEMPLATE =
  "Hello {{customerName}},\n\nYou have a new payment request from {{businessName}} for {{amount}}.\n\nPayment due: {{dueDate}}\n\nPlease transfer the amount to the following bank account:\n\nAccount Name: {{accountName}}\n\nAccount Number: {{accountNumber}}\n\nSort Code: {{sortCode}}\n\nThank you!";

export const DEFAULT_PAYMENT_REMINDER_TEMPLATE =
  "Hello {{customerName}},\n\nThis is a friendly reminder that you have an outstanding payment of {{amount}} owed to {{businessName}}.\n\nPayment due: {{dueDate}}\n\nPlease transfer the amount to the following bank account:\n\nAccount Name: {{accountName}}\n\nAccount Number: {{accountNumber}}\n\nSort Code: {{sortCode}}\n\nIf you have already made this payment, please disregard this message.\n\nThank you!";

// Bump when default templates change so saved settings get re-seeded for all accounts.
const TEMPLATE_DEFAULTS_VERSION = 2;

export const DEFAULT_PAYMENT_RECEIVED_TEMPLATE =
  "Hello {{customerName}},\n\nThank you! We've received your payment of {{amount}} to {{businessName}}.\n\nYour PDF receipt is attached for your records.\n\nWe appreciate your business!";

export const DEFAULT_ESTIMATE_TEMPLATE =
  "Hello {{customerName}},\n\nThank you for your enquiry with {{businessName}}. Please find your estimate of {{amount}} attached.\n\nThe attached PDF is for your records — this is an estimate only and not a request for payment.\n\nLet us know if you'd like to proceed and we'll send a payment request.\n\nThank you!";

export const DEFAULT_PAYMENT_SETTINGS: PaymentSettings = {
  paymentRequestTemplate: DEFAULT_PAYMENT_REQUEST_TEMPLATE,
  paymentReminderTemplate: DEFAULT_PAYMENT_REMINDER_TEMPLATE,
  paymentReceivedTemplate: DEFAULT_PAYMENT_RECEIVED_TEMPLATE,
  estimateTemplate: DEFAULT_ESTIMATE_TEMPLATE,
  includeLogo: true,
  includeAddress: true,
  includeContactInfo: true,
  includeTaxId: false,
  taxId: "GB123456789",
  includeThankYouMessage: true,
  customMessage: "Thank you for your business!",
  reminderIntervalDays: 0,
  paymentDueDays: 0,
  smsDisabled: false,
};

const storageKey = (userId?: string | null) =>
  `hound:payment-settings:${userId ?? "anon"}`;

export const loadPaymentSettings = (userId?: string | null): PaymentSettings => {
  if (typeof window === "undefined") return DEFAULT_PAYMENT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return DEFAULT_PAYMENT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<PaymentSettings> & { __templatesVersion?: number };
    const merged: PaymentSettings = { ...DEFAULT_PAYMENT_SETTINGS, ...parsed };
    // Migrate saved templates whenever defaults change so the new
    // "Payment due" line is rolled out to every existing account.
    if ((parsed.__templatesVersion ?? 0) < TEMPLATE_DEFAULTS_VERSION) {
      merged.paymentRequestTemplate = DEFAULT_PAYMENT_REQUEST_TEMPLATE;
      merged.paymentReminderTemplate = DEFAULT_PAYMENT_REMINDER_TEMPLATE;
      try {
        window.localStorage.setItem(
          storageKey(userId),
          JSON.stringify({ ...merged, __templatesVersion: TEMPLATE_DEFAULTS_VERSION })
        );
      } catch {
        /* ignore persistence error */
      }
    }
    return merged;
  } catch {
    return DEFAULT_PAYMENT_SETTINGS;
  }
};

export const savePaymentSettings = (
  settings: PaymentSettings,
  userId?: string | null
) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      storageKey(userId),
      JSON.stringify({ ...settings, __templatesVersion: TEMPLATE_DEFAULTS_VERSION })
    );
  } catch (err) {
    console.error("Failed to persist payment settings", err);
  }
};

export const usePaymentSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<PaymentSettings>(() =>
    loadPaymentSettings(user?.id)
  );

  // Hydrate from Supabase (source of truth across devices)
  useEffect(() => {
    if (!user?.id) {
      setSettings(loadPaymentSettings(null));
      return;
    }
    let cancelled = false;
    // immediate local cache
    setSettings(loadPaymentSettings(user.id));
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("receipt_settings")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error("Failed to load payment settings from Supabase", error);
        return;
      }
      const remote = (data?.receipt_settings ?? null) as Partial<PaymentSettings> | null;
      if (remote && typeof remote === "object") {
        const merged: PaymentSettings = { ...DEFAULT_PAYMENT_SETTINGS, ...remote };
        setSettings(merged);
        savePaymentSettings(merged, user.id);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const save = useCallback(
    async (next: PaymentSettings) => {
      // optimistic local update + cache
      savePaymentSettings(next, user?.id);
      setSettings(next);
      if (!user?.id) return;
      const { error } = await supabase
        .from("profiles")
        .update({ receipt_settings: next as any })
        .eq("id", user.id);
      if (error) {
        console.error("Failed to persist payment settings to Supabase", error);
        throw error;
      }
    },
    [user?.id]
  );

  return { settings, save };
};

